import { PT_PER_CSS_PX } from './exportLabelTypes'
import { PIN_LABEL } from './labelLayerStyle'
import {
  EXPORT_PAPER_PRESETS,
  ExportPaperPreset,
  PAPER_INCHES,
} from './mapExportPaper'

// Easy to read: a 0.2° x-height for Inter (Legge & Bigelow).
const READ_CM_PER_PT = 5.5
// The reading scale N is the easy-reading distance over the one a Workflow name reads at ×1, so
// every printed size is a function of N alone, the same on every sheet.
const READ_CM_AT_SCALE_1 = PIN_LABEL.sizePx * PT_PER_CSS_PX * READ_CM_PER_PT

// The distances the slider stops at. Stored without a unit, so a unit flip never changes the size.
export const READ_SCALE_RUNGS = [
  0.75, 0.85, 1, 1.1, 1.2, 1.35, 1.5, 1.75, 2, 2.25, 2.6, 3, 3.5, 4, 4.5, 5,
  5.5,
]

interface IReadScaleSheet {
  // The distance the whole sheet is read at: a Letter is held, a Tabloid lies on a desk, an A2 is
  // pinned up; a larger sheet is read in parts, so it levels off there.
  standard: number
  // Above N 1.5 the key, band, logo and credits are fixed; pins, Location icons and the key's marks
  // grow to ×2 at N 2; past that only names, the name floor and group disks grow. So the maximum is
  // set by how much of the sheet a name takes and the distance in sheet diagonals, not by fit.
  maximum: number
}

const HELD: IReadScaleSheet = { standard: 1, maximum: 3 }
const ON_DESK: IReadScaleSheet = { standard: 1.2, maximum: 3.5 }
const PINNED_UP: IReadScaleSheet = { standard: 1.5, maximum: 3.5 }
const ON_PLAN_TABLE: IReadScaleSheet = { standard: 1.5, maximum: 4.5 }
const ROOM_BOARD: IReadScaleSheet = { standard: 1.5, maximum: 5.5 }

// The short side places a sheet: each family steps by halving its area, so every sheet sits well
// clear of a boundary.
export const sheetClass = (shortIn: number): IReadScaleSheet => {
  if (shortIn < 10) return HELD
  if (shortIn < 14) return ON_DESK
  if (shortIn < 20) return PINNED_UP
  if (shortIn < 28) return ON_PLAN_TABLE
  return ROOM_BOARD
}

export const READ_SCALE_BY_SHEET = Object.fromEntries(
  EXPORT_PAPER_PRESETS.map(paper => [
    paper,
    sheetClass(PAPER_INCHES[paper][1]),
  ]),
) as Record<ExportPaperPreset, IReadScaleSheet>

export const readScaleRungs = (paper: ExportPaperPreset): number[] =>
  READ_SCALE_RUNGS.filter(rung => rung <= READ_SCALE_BY_SHEET[paper].maximum)

export interface IExportReadDistance {
  value: number
  unit: 'cm' | 'm' | 'in' | 'ft'
}

const CM_PER_INCH = 2.54
const INCHES_PER_FOOT = 12

// Five centimetres below a metre, then tenths of a metre; whole inches below 3 ft, then half feet.
export function readScaleDistance(
  readScale: number,
  isMetric: boolean,
): IExportReadDistance {
  const cm = readScale * READ_CM_AT_SCALE_1
  // Rounded before the unit is picked, so 99 cm reads 1 m, not 100 cm.
  if (isMetric) {
    const roundedCm = Math.round(cm / 5) * 5
    return roundedCm < 100
      ? { value: roundedCm, unit: 'cm' }
      : { value: Math.round(cm / 10) / 10, unit: 'm' }
  }
  const inches = cm / CM_PER_INCH
  const roundedInches = Math.round(inches)
  return roundedInches < 3 * INCHES_PER_FOOT
    ? { value: roundedInches, unit: 'in' }
    : { value: Math.round((2 * inches) / INCHES_PER_FOOT) / 2, unit: 'ft' }
}
